import React from "react";
import { useNavigate } from "react-router";
import { User, Star, ChevronRight, Calendar, MapPin } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { Lead } from "../types";
import { LeadStatusBadge } from "./LeadStatusBadge";
import { getScanTargetLabel } from "../services/codeRecognitionService";
import { markLabel } from "../utils/contactMarks";
import { formatAnnotationNotesPreview } from "../utils/annotationNotes";

interface LeadCardProps {
  lead: Lead;
  basePath?: string;
}

function formatDate(iso: string, locale: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString(locale.startsWith("en") ? "en-GB" : "ru-RU", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function LeadCard({ lead, basePath = "/exhibitor/leads" }: LeadCardProps) {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const notesPreview = formatAnnotationNotesPreview(lead.notes, lead.tags ?? []);

  return (
    <button
      onClick={() => navigate(`${basePath}/${lead.id}`)}
      className="w-full p-4 rounded-2xl bg-white border border-gray-100 hover:border-indigo-200 hover:shadow-md transition-all text-left"
    >
      <div className="flex items-start gap-3">
        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center flex-shrink-0">
          <User size={20} className="text-indigo-600" />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between mb-1">
            <h3 className="text-[15px] font-medium text-gray-900 truncate">
              {lead.visitorName}
            </h3>
            {lead.rating && (
              <div className="flex items-center gap-0.5 flex-shrink-0 ml-2">
                <Star size={12} className="text-amber-500 fill-amber-500" />
                <span className="text-[12px] text-amber-600 font-medium">{lead.rating}</span>
              </div>
            )}
          </div>

          <div className="flex items-center gap-2 mb-2">
            <span className="text-[12px] text-gray-500">
              {getScanTargetLabel(lead.scanType, t)}
            </span>
            <span className="text-[11px] text-gray-400">·</span>
            <div className="flex items-center gap-1 text-[12px] text-gray-400">
              <Calendar size={11} />
              <span>{formatDate(lead.scannedAt, i18n.language)}</span>
            </div>
          </div>

          {lead.visitorCompany ? (
            <p className="text-[12px] text-gray-600 mb-1 truncate">{lead.visitorCompany}</p>
          ) : null}
          {lead.visitorPosition ? (
            <p className="text-[12px] text-gray-500 mb-1 truncate">{lead.visitorPosition}</p>
          ) : null}
          {lead.visitorCity ? (
            <p className="flex items-center gap-1 text-[12px] text-gray-500 mb-1 truncate">
              <MapPin size={11} className="flex-shrink-0" />
              <span>{lead.visitorCity}</span>
            </p>
          ) : null}
          {lead.assignedRepName ? (
            <p className="text-[11px] text-slate-500 mb-1 truncate">
              {t("leads.col_assignee")}: {lead.assignedRepName}
            </p>
          ) : null}
          {(lead.visitorPhone || lead.visitorEmail) && (
            <p className="text-[12px] text-indigo-600 mb-2 truncate">
              {[lead.visitorPhone, lead.visitorEmail].filter(Boolean).join(" · ")}
            </p>
          )}
          {notesPreview ? (
            <p className="text-[13px] text-gray-600 line-clamp-2 mb-2">
              {notesPreview}
            </p>
          ) : null}

          <div className="flex items-center justify-between">
            <LeadStatusBadge status={lead.status} size="sm" />
            {lead.tags && lead.tags.length > 0 && (
              <div className="flex gap-1 flex-wrap justify-end max-w-[55%]">
                {lead.tags.slice(0, 3).map((tag) => (
                  <span
                    key={tag}
                    className="text-[10px] text-indigo-700 bg-indigo-50 px-1.5 py-0.5 rounded"
                  >
                    {markLabel(tag, t)}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        <ChevronRight size={16} className="text-gray-300 flex-shrink-0 mt-1" />
      </div>
    </button>
  );
}
